<template>
  <div class="fa-user-view">
    <div v-if="loading" style="padding: 20px; text-align: center;">
      <b-spinner/>
    </div>
    <template v-else>
      <!-- header -->
      <div class="fa-user-header">
        <div>
          <div class="fa-user-id">{{ userData.user_id }}</div>
          <div style="font-size: 12px; color: #888;">{{ userData.tenant_id }} · {{ userData.period }}</div>
          <div v-if="userData.cross_tenant" class="fa-cross-tenant-note">
            Showing chats across all tenants this admin has access to. Cost is billed to {{ userData.tenant_id }}; conversations live under each customer tenant.
          </div>
        </div>
      </div>

      <!-- usage card -->
      <div class="fa-user-card">
        <div class="fa-stat">
          <div class="fa-stat-label">Blended cost</div>
          <div class="fa-stat-value">{{ formatCents(userData.blended_cost_cents) }}</div>
        </div>
        <div class="fa-stat">
          <div class="fa-stat-label">Chat / Docs</div>
          <div class="fa-stat-value">{{ userData.chat_requests || 0 }}<span class="fa-stat-sub"> / {{ userData.doc_categorizations || 0 }}</span></div>
        </div>
        <div class="fa-stat">
          <div class="fa-stat-label">Input tokens</div>
          <div class="fa-stat-value">{{ formatTokens(userData.input_tokens) }}</div>
        </div>
        <div class="fa-stat">
          <div class="fa-stat-label">Output tokens</div>
          <div class="fa-stat-value">{{ formatTokens(userData.output_tokens) }}</div>
        </div>
      </div>

      <!-- live sessions (1 in single-tenant view; potentially many in cross-tenant) -->
      <div class="fa-section">
        <div class="fa-section-title">{{ allLiveSessions.length > 1 ? 'Live sessions (' + allLiveSessions.length + ')' : 'Live session' }}</div>
        <div v-if="allLiveSessions.length > 0">
          <div v-for="live in allLiveSessions"
               :key="live.session_id || live.home_tenant_id"
               class="fa-session-row"
               @click="openLiveSession(live)">
            <div style="flex: 1;">
              <div style="font-weight: 600;">{{ liveTurnCount(live) }} turns</div>
              <div style="font-size: 12px; color: #888;">Updated {{ formatEpoch(live.updated_at) }}</div>
            </div>
            <span v-if="userData.cross_tenant" class="fa-home-tenant-chip">{{ tenantLabel(live.home_tenant_id || live.tenant_id) }}</span>
            <i class="fa fa-chevron-right" style="color: #888;"/>
          </div>
        </div>
        <div v-else style="color: #888; font-size: 13px; padding: 8px;">No live session.</div>
      </div>

      <!-- archived sessions -->
      <div class="fa-section">
        <div class="fa-section-title">Archived sessions ({{ (userData.archived_sessions || []).length }})</div>
        <div v-if="(userData.archived_sessions || []).length > 0">
          <div v-for="s in userData.archived_sessions"
               :key="s.session_id"
               class="fa-session-row"
               @click="openArchivedSession(s)">
            <div style="flex: 1;">
              <div style="font-weight: 600;">{{ s.turn_count }} turns</div>
              <!-- lead with last activity, not archived_at: the list is scoped to
                   the month the conversation happened in, and a session cleared
                   weeks later would otherwise be captioned with a date outside
                   the selected month. archive date stays one hover away. -->
              <div style="font-size: 12px; color: #888;"
                   :title="s.archived_at ? 'Archived ' + formatEpoch(s.archived_at) : ''">
                Updated {{ formatEpoch(s.updated_at || s.archived_at) }}
              </div>
            </div>
            <span v-if="userData.cross_tenant" class="fa-home-tenant-chip">{{ tenantLabel(s.home_tenant_id || s.tenant_id) }}</span>
            <i class="fa fa-chevron-right" style="color: #888;"/>
          </div>
        </div>
        <div v-else style="color: #888; font-size: 13px; padding: 8px;">None yet.</div>
      </div>
    </template>
  </div>
</template>

<script>
import { formatCents, formatEpoch, formatTokens } from '../../helpers/fa_admin'

export default {
  name: 'FaUserView',
  props: {
    userData: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    tenantLabels: { type: Object, default: () => ({}) },
  },
  computed: {
    allLiveSessions() {
      const live = (this.userData && this.userData.live_session) || {}
      // cross-tenant payload carries the full list under live_sessions; the
      // single-tenant payload just sets exists+turns on the live object itself.
      const list = Array.isArray(live.live_sessions) && live.live_sessions.length > 0
        ? live.live_sessions
        : (live.exists ? [live] : [])
      // already sorted server-side, but defensive sort just in case.
      return list.slice().sort((a, b) => Number(b.updated_at || 0) - Number(a.updated_at || 0))
    },
  },
  methods: {
    formatCents,
    formatEpoch,
    formatTokens,
    tenantLabel(tenantId) {
      if (!tenantId) return ''
      return this.tenantLabels[tenantId] || tenantId
    },
    liveTurnCount(live) {
      if (!live) return 0
      if (Array.isArray(live.turns)) return live.turns.length
      if (typeof live.turn_count === 'number') return live.turn_count
      return 0
    },
    openLiveSession(live) {
      // route to the tenant where the session actually lives, not the support
      // billing tenant the admin is viewing through.
      const homeTenant = live.home_tenant_id || live.tenant_id || this.userData.tenant_id
      this.$emit('open-session', {
        tenant_id: homeTenant,
        user_id: this.userData.user_id,
        session_id: live.session_id,
      })
    },
    openArchivedSession(s) {
      const homeTenant = s.home_tenant_id || s.tenant_id
      this.$emit('open-session', {
        tenant_id: homeTenant,
        user_id: s.user_id,
        session_id: s.session_id,
        archived: true,
      })
    },
  },
}
</script>

<style scoped>
.fa-user-view { padding: 8px 0; }

.fa-user-header {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
}
.fa-user-id {
  font-size: 20px;
  font-weight: bold;
  color: #333;
}
.fa-cross-tenant-note {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
  font-style: italic;
  max-width: 640px;
}

.fa-user-card {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 16px;
}
.fa-stat { min-width: 120px; }
.fa-stat-label {
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.fa-stat-value {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}
.fa-stat-sub {
  font-size: 13px;
  font-weight: normal;
  color: #888;
}

.fa-section { margin-top: 16px; }
.fa-section-title {
  font-size: 14px;
  font-weight: bold;
  color: var(--fi-rail-accent);
  margin-bottom: 6px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 4px;
}
.fa-session-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-bottom: 6px;
  cursor: pointer;
  background: #fff;
}
.fa-session-row:hover {
  background: #f9fafb;
  border-color: #c8d3dc;
}
.fa-home-tenant-chip {
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  color: var(--fi-rail-accent);
  background: #eef3f7;
  border: 1px solid #d6e1ea;
  border-radius: 999px;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
